import { z } from 'zod'
import { type IAPEntitlement } from '../entitlements'
import { CreditScoreSchema } from '../resident'
import { PermitStatusSchema } from './firstAmerican'

export const ResidentPremiumDataSchema = z.object({
  age: z.number().min(0).optional(),
  creditScore: CreditScoreSchema.optional(),
})

export const PermitLabelSchema = z.enum([
  'other',
  'accessory_dwelling_unit',
  'electric_vehicle_charger',
  'electric_water_heater',
  'electrical_panel_upgrade',
  'home_addition',
  'hvac_changeout',
  'pool',
  'roof',
  'solar',
  'spa_sauna',
])
export type PermitLabel = z.infer<typeof PermitLabelSchema>

export const permitLabelEntitlement: Record<PermitLabel, IAPEntitlement | undefined> = {
  accessory_dwelling_unit: 'permit_data_adu',
  electric_vehicle_charger: 'permit_data_ev',
  electric_water_heater: 'permit_data_electric_water_heater',
  home_addition: 'permit_data_home_addition',
  pool: 'permit_data_pool',
  roof: 'permit_data_roof',
  solar: 'permit_data_solar',
  spa_sauna: 'permit_data_spa',
  electrical_panel_upgrade: 'permit_data_electrical_panel',
  hvac_changeout: 'permit_data_hvac',
  other: undefined,
}

export const PermitDataSchema = z.object({
  label: PermitLabelSchema,
  contractorId: z.string().optional(),
  contractorName: z.string().optional(),
  initStatusDate: z.number().optional(),
  initStatus: PermitStatusSchema.optional(),
  latestStatusDate: z.number().optional(),
  latestStatus: PermitStatusSchema.optional(),
  cancelledDate: z.number().optional(),
  isBankrupt: z.boolean().optional(),
})
export type PermitData = z.infer<typeof PermitDataSchema>

export const PaceLienDataSchema = z.object({
  paceId: z.string().optional(),
  authorityName: z.string().optional(),
  recordingDate: z.number().optional(),
  isBankrupt: z.boolean().optional(),
})
export type PaceLienData = z.infer<typeof PaceLienDataSchema>

export const InvLienDataSchema = z.object({
  transactionId: z.string().optional(),
  recordingDate: z.number().optional(),
  party1Name: z.string().optional(),
  party2Name: z.string().optional(),
  isBankrupt: z.boolean().optional(),
})
export type InvLienData = z.infer<typeof InvLienDataSchema>

export const PremiumLocationAttributesSchema = z.object({
  permitCount: z.number().optional().describe('The unfiltered count of permits'),
  permits: z.array(PermitDataSchema).optional().describe('The permits filtered by entitlements'),
  paceLienCount: z.number().optional(),
  paceLiens: z.array(PaceLienDataSchema).optional(),
  invLienCount: z.number().optional(),
  invLiens: z.array(InvLienDataSchema).optional(),
  wirecenterOperator: z.string().optional(),
})
export type PremiumLocationAttributes = z.infer<typeof PremiumLocationAttributesSchema>

export const HIGH_ENERGY_USE_PERMITS: PermitLabel[] = [
  'pool',
  'electric_vehicle_charger',
  'spa_sauna',
  'hvac_changeout',
  'electric_water_heater',
]

export const HOME_UPGRADE_PERMITS: PermitLabel[] = [
  'home_addition',
  'electrical_panel_upgrade',
  'accessory_dwelling_unit',
  'roof',
]

export const SOLAR_PERMITS: PermitLabel[] = ['solar']
